import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 22: MAATI
 * Style: Warm Heritage Artisan
 * Best for: Indian handloom, Artisan fashion, Heritage textiles, Ethnic wear
 * Warm brown/cream/terracotta palette celebrating Indian craft traditions
 */
const Template22 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);
    const categoriesRef = useRef(null);

    // Craft Categories
    const categories = [
        {
            name: 'Chanderi',
            description: 'Sheer elegance woven in silk and cotton',
            image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
            name: 'Banarasi',
            description: 'Timeless zari work from the holy city',
            image: 'https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
            name: 'Khadi',
            description: 'The fabric of freedom, spun by hand',
            image: 'https://images.pexels.com/photos/6634454/pexels-photo-6634454.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
            name: 'Block Print',
            description: 'Ancient stamps, modern stories',
            image: 'https://images.pexels.com/photos/4862910/pexels-photo-4862910.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
            name: 'Chikankari',
            description: 'Delicate thread-work from Lucknow',
            image: 'https://images.pexels.com/photos/7442282/pexels-photo-7442282.jpeg?auto=compress&cs=tinysrgb&w=800'
        }
    ];

    // Products
    const products = [
        { id: 1, name: 'Chanderi Silk Saree', price: '\u20B98,999', badge: 'New', image: 'https://images.pexels.com/photos/28943572/pexels-photo-28943572.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 2, name: 'Block Print Kurta Set', price: '\u20B94,500', badge: null, image: 'https://images.pexels.com/photos/5595710/pexels-photo-5595710.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 3, name: 'Khadi Cotton Dupatta', price: '\u20B92,200', badge: null, image: 'https://images.pexels.com/photos/6276062/pexels-photo-6276062.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 4, name: 'Banarasi Zari Lehenga', price: '\u20B915,999', badge: 'New', image: 'https://images.pexels.com/photos/9901808/pexels-photo-9901808.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 5, name: 'Chikankari Anarkali', price: '\u20B96,800', badge: null, image: 'https://images.pexels.com/photos/17611651/pexels-photo-17611651.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 6, name: 'Handloom Linen Kurta', price: '\u20B93,500', badge: null, image: 'https://images.pexels.com/photos/13650900/pexels-photo-13650900.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 7, name: 'Ikat Silk Stole', price: '\u20B94,200', badge: 'New', image: 'https://images.pexels.com/photos/4219107/pexels-photo-4219107.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 8, name: 'Ajrakh Print Dress', price: '\u20B95,800', badge: null, image: 'https://images.pexels.com/photos/7516956/pexels-photo-7516956.jpeg?auto=compress&cs=tinysrgb&w=800' }
    ];

    // Process Steps
    const processSteps = [
        {
            step: '01',
            title: 'Design',
            description: 'Every motif is hand-sketched, drawing from centuries of regional craft traditions.',
            icon: (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#C4572A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 36l4-16L36 8l-4 16z" />
                    <path d="M28 20l-8 8" />
                    <circle cx="24" cy="24" r="2" />
                </svg>
            )
        },
        {
            step: '02',
            title: 'Weave',
            description: 'Artisans at the loom bring patterns to life, thread by patient thread.',
            icon: (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#C4572A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="8" y="8" width="32" height="32" rx="2" />
                    <line x1="8" y1="16" x2="40" y2="16" />
                    <line x1="8" y1="24" x2="40" y2="24" />
                    <line x1="8" y1="32" x2="40" y2="32" />
                    <line x1="16" y1="8" x2="16" y2="40" />
                    <line x1="24" y1="8" x2="24" y2="40" />
                    <line x1="32" y1="8" x2="32" y2="40" />
                </svg>
            )
        },
        {
            step: '03',
            title: 'Dye',
            description: 'Natural pigments from earth and plant give each piece its soul.',
            icon: (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#C4572A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M24 6c0 0-12 14-12 22a12 12 0 0024 0c0-8-12-22-12-22z" />
                    <path d="M18 30a6 6 0 006 6" />
                </svg>
            )
        },
        {
            step: '04',
            title: 'Finish',
            description: 'Each garment is inspected, pressed, and blessed before it reaches you.',
            icon: (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#C4572A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6l4 8 4-8" />
                    <path d="M14 14h20v4c0 8-4 14-10 18c-6-4-10-10-10-18v-4z" />
                    <polyline points="19 26 22 29 29 22" />
                </svg>
            )
        }
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The Chanderi saree I received is a masterpiece. You can feel the hours of handwork in every thread. It is not just clothing \u2014 it is art that tells a story.',
            name: 'Ananya Sharma',
            city: 'Jaipur'
        },
        {
            text: 'MAATI reconnected me to my roots. Their Khadi kurtas are softer than anything I have owned, and knowing an artisan wove it makes it priceless.',
            name: 'Vikram Iyer',
            city: 'Bengaluru'
        },
        {
            text: 'I bought the Banarasi lehenga for my wedding and every guest asked about it. The zari work was breathtaking. True heritage craftsmanship.',
            name: 'Meera Patel',
            city: 'Ahmedabad'
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    const scrollCategories = (direction) => {
        if (categoriesRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            categoriesRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setEmail('');
        }
    };

    return (
        <div style={{ fontFamily: "'Source Sans 3', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');
            `}</style>

            {/* ===== SECTION 1: CRAFT HERO ===== */}
            <section className="relative min-h-screen w-full overflow-hidden flex items-center justify-center">
                {/* Background Image */}
                <img
                    src="https://images.pexels.com/photos/6634704/pexels-photo-6634704.jpeg?auto=compress&cs=tinysrgb&w=1920"
                    alt="Handloom weaving"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Warm Cream Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(250,246,241,0.75) 0%, rgba(250,246,241,0.6) 40%, rgba(250,246,241,0.85) 100%)'
                    }}
                />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6 max-w-3xl mx-auto">
                    <p
                        className="text-xs sm:text-sm tracking-[0.35em] uppercase mb-6"
                        style={{ color: '#C4572A', fontFamily: "'Source Sans 3', sans-serif", fontWeight: 600 }}
                    >
                        Handcrafted Indian Textiles
                    </p>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light mb-6"
                        style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            color: '#5C3D2E',
                            lineHeight: 1.15,
                            fontWeight: 400
                        }}
                    >
                        Woven by Hand,<br />Worn with Pride
                    </h1>
                    <p
                        className="text-base sm:text-lg lg:text-xl font-light mb-10 max-w-xl mx-auto"
                        style={{
                            color: '#8B7355',
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontStyle: 'italic',
                            lineHeight: 1.6
                        }}
                    >
                        Every thread carries a story. Every weave preserves a tradition.
                    </p>
                    <button
                        onClick={() => navigate('/template22/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: '#C4572A',
                            border: 'none',
                            color: '#FFFFFF',
                            fontFamily: "'Source Sans 3', sans-serif",
                            fontWeight: 600,
                            borderRadius: '2px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#D4683B';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#C4572A';
                        }}
                    >
                        Discover Our Craft <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-12 opacity-50" style={{ backgroundColor: '#C4572A' }}>
                        <div
                            className="w-full h-1/2"
                            style={{
                                backgroundColor: '#C4572A',
                                animation: 'maatiScrollPulse 2s ease-in-out infinite'
                            }}
                        />
                    </div>
                </div>
                <style>{`
                    @keyframes maatiScrollPulse {
                        0%, 100% { opacity: 0.3; transform: translateY(0); }
                        50% { opacity: 1; transform: translateY(12px); }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 2: CRAFT CATEGORIES ===== */}
            <section style={{ backgroundColor: '#FAF6F1' }} className="py-16 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#C4572A', fontFamily: "'Source Sans 3', sans-serif", fontWeight: 600 }}
                        >
                            Our Traditions
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl font-light"
                            style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#5C3D2E', fontWeight: 400 }}
                        >
                            Explore the Crafts
                        </h2>
                    </div>

                    {/* Scroll Buttons (desktop) */}
                    <div className="relative">
                        <button
                            onClick={() => scrollCategories('left')}
                            className="maati-cat-scroll-btn absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
                            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8DDD0', boxShadow: '0 2px 8px rgba(92,61,46,0.1)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; e.currentTarget.querySelector('svg').style.color = '#FFFFFF'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.querySelector('svg').style.color = '#5C3D2E'; }}
                        >
                            <ChevronLeft size={20} style={{ color: '#5C3D2E', transition: 'color 0.3s ease' }} />
                        </button>
                        <button
                            onClick={() => scrollCategories('right')}
                            className="maati-cat-scroll-btn absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
                            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8DDD0', boxShadow: '0 2px 8px rgba(92,61,46,0.1)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; e.currentTarget.querySelector('svg').style.color = '#FFFFFF'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.querySelector('svg').style.color = '#5C3D2E'; }}
                        >
                            <ChevronRight size={20} style={{ color: '#5C3D2E', transition: 'color 0.3s ease' }} />
                        </button>

                        {/* Scrollable Row */}
                        <div
                            ref={categoriesRef}
                            className="flex gap-6 overflow-x-auto pb-4 maati-hide-scrollbar"
                            style={{ scrollSnapType: 'x mandatory' }}
                        >
                            {categories.map((category, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate('/template22/shop')}
                                    className="flex-shrink-0 cursor-pointer group"
                                    style={{
                                        width: '240px',
                                        scrollSnapAlign: 'start',
                                    }}
                                >
                                    <div
                                        className="relative overflow-hidden mb-4"
                                        style={{
                                            borderRadius: '12px',
                                            border: '1px solid #E8DDD0',
                                            aspectRatio: '3/4',
                                        }}
                                    >
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div
                                            className="absolute inset-0"
                                            style={{
                                                background: 'linear-gradient(to top, rgba(92,61,46,0.7) 0%, transparent 60%)'
                                            }}
                                        />
                                        <div className="absolute bottom-0 left-0 right-0 p-5">
                                            <h3
                                                className="text-xl font-medium mb-1"
                                                style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#FFFFFF' }}
                                            >
                                                {category.name}
                                            </h3>
                                            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                                {category.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <style>{`
                    .maati-hide-scrollbar::-webkit-scrollbar { display: none; }
                    .maati-hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    .maati-cat-scroll-btn { display: none; }
                    @media (min-width: 1024px) {
                        .maati-cat-scroll-btn { display: flex; }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 3: ARTISAN STORY STRIP ===== */}
            <section className="relative overflow-hidden" style={{ backgroundColor: '#5C3D2E' }}>
                {/* Mobile Layout */}
                <div className="lg:hidden">
                    <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/6634704/pexels-photo-6634704.jpeg?auto=compress&cs=tinysrgb&w=1200"
                            alt="Artisan at the loom"
                            className="w-full h-full object-cover"
                            style={{ opacity: 0.8 }}
                        />
                    </div>
                    <div className="px-6 py-10 text-center">
                        <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: '#C4572A' }}>
                            Our Heritage
                        </p>
                        <blockquote
                            className="text-2xl font-light mb-6 leading-relaxed"
                            style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#F3ECE4', fontStyle: 'italic' }}
                        >
                            "We do not just make garments. We keep alive the songs our grandmothers hummed at the loom."
                        </blockquote>
                        <p className="text-sm mb-6" style={{ color: '#E8DDD0' }}>
                            &mdash; Lakshmi Devi, Master Weaver, Maheshwar
                        </p>
                        <button
                            onClick={() => navigate('/template22/about')}
                            className="inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase transition-colors duration-300"
                            style={{ color: '#C4572A', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#D4683B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#C4572A')}
                        >
                            Meet Our Artisans <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden lg:flex items-stretch" style={{ minHeight: '500px' }}>
                    <div className="w-1/2 relative overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/6634704/pexels-photo-6634704.jpeg?auto=compress&cs=tinysrgb&w=1200"
                            alt="Artisan at the loom"
                            className="w-full h-full object-cover"
                            style={{ opacity: 0.85 }}
                        />
                    </div>
                    <div className="w-1/2 flex flex-col justify-center px-16 xl:px-24 py-16">
                        <p className="text-xs tracking-[0.3em] uppercase mb-6" style={{ color: '#C4572A', fontWeight: 600 }}>
                            Our Heritage
                        </p>
                        <blockquote
                            className="text-3xl xl:text-4xl font-light mb-8 leading-snug"
                            style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#F3ECE4', fontStyle: 'italic' }}
                        >
                            "We do not just make garments. We keep alive the songs our grandmothers hummed at the loom."
                        </blockquote>
                        <p className="text-sm mb-8" style={{ color: '#E8DDD0' }}>
                            &mdash; Lakshmi Devi, Master Weaver, Maheshwar
                        </p>
                        <button
                            onClick={() => navigate('/template22/about')}
                            className="inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase transition-colors duration-300 self-start"
                            style={{ color: '#C4572A', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: "'Source Sans 3', sans-serif" }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#D4683B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#C4572A')}
                        >
                            Meet Our Artisans <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCT GRID ===== */}
            <section style={{ backgroundColor: '#FAF6F1' }} className="py-16 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#C4572A', fontFamily: "'Source Sans 3', sans-serif", fontWeight: 600 }}
                        >
                            Curated Collection
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl font-light mb-4"
                            style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#5C3D2E', fontWeight: 400 }}
                        >
                            Handpicked for You
                        </h2>
                        <p className="text-sm max-w-md mx-auto" style={{ color: '#8B7355' }}>
                            Each piece is made by hand, carrying the warmth of the artisan who created it.
                        </p>
                    </div>

                    {/* Product Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template22/shop')}
                                className="group cursor-pointer"
                            >
                                <div
                                    className="relative overflow-hidden mb-3"
                                    style={{
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #E8DDD0',
                                        borderRadius: '4px',
                                        aspectRatio: '3/4',
                                        transition: 'border-color 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C4572A'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8DDD0'; }}
                                >
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />

                                    {/* Badge */}
                                    {product.badge && (
                                        <span
                                            className="absolute top-3 left-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider"
                                            style={{
                                                backgroundColor: '#C4572A',
                                                color: '#FFFFFF',
                                                borderRadius: '2px',
                                                fontFamily: "'Source Sans 3', sans-serif",
                                            }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}

                                    {/* Wishlist */}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
                                        style={{
                                            backgroundColor: wishlist[product.id] ? '#C4572A' : 'rgba(255,255,255,0.9)',
                                            border: '1px solid #E8DDD0',
                                        }}
                                    >
                                        <Heart
                                            size={16}
                                            fill={wishlist[product.id] ? '#FFFFFF' : 'none'}
                                            style={{ color: wishlist[product.id] ? '#FFFFFF' : '#5C3D2E' }}
                                        />
                                    </button>
                                </div>

                                <h3
                                    className="text-sm sm:text-base font-medium mb-1"
                                    style={{ fontFamily: "'Source Sans 3', sans-serif", color: '#5C3D2E' }}
                                >
                                    {product.name}
                                </h3>
                                <p
                                    className="text-sm font-semibold"
                                    style={{ color: '#C4572A', fontFamily: "'Source Sans 3', sans-serif" }}
                                >
                                    {product.price}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* View All */}
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template22/shop')}
                            className="inline-flex items-center gap-2 px-10 py-3.5 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: 'transparent',
                                border: '1.5px solid #C4572A',
                                color: '#C4572A',
                                fontFamily: "'Source Sans 3', sans-serif",
                                fontWeight: 600,
                                borderRadius: '2px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#C4572A';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#C4572A';
                            }}
                        >
                            View All Collections <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: PROCESS SECTION ===== */}
            <section style={{ backgroundColor: '#F3ECE4' }} className="py-16 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#C4572A', fontFamily: "'Source Sans 3', sans-serif", fontWeight: 600 }}
                        >
                            From Loom to You
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl font-light"
                            style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#5C3D2E', fontWeight: 400 }}
                        >
                            The Journey of Each Piece
                        </h2>
                    </div>

                    <div className="maati-process-grid">
                        {processSteps.map((step, index) => (
                            <div key={index} className="text-center relative">
                                {/* Connector Line (desktop) */}
                                {index < processSteps.length - 1 && (
                                    <div
                                        className="maati-process-connector"
                                        style={{
                                            position: 'absolute',
                                            top: '40px',
                                            right: '-50%',
                                            width: '100%',
                                            height: '1px',
                                            backgroundColor: '#E8DDD0',
                                        }}
                                    />
                                )}

                                {/* Icon */}
                                <div
                                    className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center relative z-10"
                                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8DDD0' }}
                                >
                                    {step.icon}
                                </div>

                                <span
                                    className="text-xs tracking-[0.2em] uppercase block mb-2"
                                    style={{ color: '#C4572A', fontWeight: 600 }}
                                >
                                    Step {step.step}
                                </span>
                                <h3
                                    className="text-2xl mb-3"
                                    style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#5C3D2E', fontWeight: 500 }}
                                >
                                    {step.title}
                                </h3>
                                <p
                                    className="text-sm leading-relaxed max-w-xs mx-auto"
                                    style={{ color: '#8B7355' }}
                                >
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
                <style>{`
                    .maati-process-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 40px 24px;
                    }
                    .maati-process-connector {
                        display: none;
                    }
                    @media (min-width: 768px) {
                        .maati-process-grid {
                            grid-template-columns: repeat(4, 1fr);
                            gap: 48px;
                        }
                        .maati-process-connector {
                            display: block !important;
                        }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            <section style={{ backgroundColor: '#FAF6F1' }} className="py-16 sm:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#C4572A', fontFamily: "'Source Sans 3', sans-serif", fontWeight: 600 }}
                        >
                            Voices
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl font-light"
                            style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#5C3D2E', fontWeight: 400 }}
                        >
                            What Our Community Says
                        </h2>
                    </div>

                    {/* Testimonial Card */}
                    <div
                        className="relative overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="text-center px-4 sm:px-12 py-10 sm:py-14"
                            style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E8DDD0',
                                borderRadius: '4px',
                            }}
                        >
                            {/* Decorative Quote */}
                            <div
                                className="text-6xl sm:text-7xl leading-none mb-6"
                                style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#E8DDD0' }}
                            >
                                &ldquo;
                            </div>

                            <p
                                className="text-lg sm:text-xl lg:text-2xl mb-8 leading-relaxed max-w-2xl mx-auto"
                                style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    color: '#5C3D2E',
                                    fontStyle: 'italic',
                                    fontWeight: 400,
                                }}
                            >
                                {testimonials[activeTestimonial].text}
                            </p>

                            <div
                                className="w-12 h-px mx-auto mb-4"
                                style={{ backgroundColor: '#C4572A' }}
                            />

                            <p
                                className="text-sm font-semibold mb-1"
                                style={{ color: '#5C3D2E', fontFamily: "'Source Sans 3', sans-serif" }}
                            >
                                {testimonials[activeTestimonial].name}
                            </p>
                            <p
                                className="text-xs"
                                style={{ color: '#8B7355', fontFamily: "'Source Sans 3', sans-serif" }}
                            >
                                {testimonials[activeTestimonial].city}
                            </p>
                        </div>

                        {/* Dots */}
                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className="transition-all duration-300"
                                    style={{
                                        width: activeTestimonial === index ? '24px' : '8px',
                                        height: '8px',
                                        borderRadius: '4px',
                                        backgroundColor: activeTestimonial === index ? '#C4572A' : '#E8DDD0',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            <section
                className="relative overflow-hidden"
                style={{ backgroundColor: '#5C3D2E' }}
            >
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center relative z-10">
                    {/* Decorative Pattern */}
                    <div
                        className="absolute inset-0 opacity-5"
                        style={{
                            backgroundImage: `repeating-linear-gradient(
                                45deg,
                                transparent,
                                transparent 20px,
                                rgba(196,87,42,0.3) 20px,
                                rgba(196,87,42,0.3) 21px
                            )`
                        }}
                    />

                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-4 relative z-10"
                        style={{ color: '#C4572A', fontWeight: 600 }}
                    >
                        Stay Connected
                    </p>
                    <h2
                        className="text-3xl sm:text-4xl font-light mb-4 relative z-10"
                        style={{ fontFamily: "'Cormorant Infant', Georgia, serif", color: '#F3ECE4', fontWeight: 400 }}
                    >
                        Join Our Craft Community
                    </h2>
                    <p
                        className="text-sm mb-8 max-w-md mx-auto relative z-10"
                        style={{ color: '#E8DDD0', lineHeight: 1.7 }}
                    >
                        Be the first to know about new collections, artisan stories, and exclusive offers from the loom.
                    </p>

                    <form
                        onSubmit={handleSubscribe}
                        className="flex gap-0 max-w-md mx-auto relative z-10 maati-newsletter-form"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            required
                            className="flex-1 px-5 py-3.5 text-sm outline-none"
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(232,221,208,0.3)',
                                borderRight: 'none',
                                color: '#F3ECE4',
                                fontFamily: "'Source Sans 3', sans-serif",
                                borderRadius: '2px 0 0 2px',
                            }}
                        />
                        <button
                            type="submit"
                            className="px-6 sm:px-8 py-3.5 text-sm font-semibold tracking-[0.1em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#C4572A',
                                color: '#FFFFFF',
                                border: 'none',
                                fontFamily: "'Source Sans 3', sans-serif",
                                borderRadius: '0 2px 2px 0',
                                cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#D4683B')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C4572A')}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
                <style>{`
                    @media (max-width: 480px) {
                        .maati-newsletter-form {
                            flex-direction: column !important;
                        }
                        .maati-newsletter-form input {
                            border-right: 1px solid rgba(232,221,208,0.3) !important;
                            border-radius: 2px !important;
                            margin-bottom: 8px;
                        }
                        .maati-newsletter-form button {
                            border-radius: 2px !important;
                        }
                    }
                `}</style>
            </section>
        </div>
    );
};

export default Template22;
